import React, { memo, useCallback, useEffect, useReducer, useMemo, useState, useRef } from 'react';

import { Button, SearchBar, Spacer, Icon } from '@Sage/hoi-poi-ui';
import { removeAccents } from '@Sage/ssm-utils';
import { SectionList } from '@Sage/ssm-web5';
import Fuse from 'fuse.js';
import { Route, Routes } from 'react-router-dom';

import DeleteModal from 'components/DeleteModal';
import IconCell from 'components/TableCells/IconCell';
import TextPopoverCell from 'components/TableCells/TextPopoverCell';
import { subscribe } from 'lib/EventBuser';
import { REFRESH_TABLE } from 'lib/events';
import Context from 'managers/Context';
import { getAutomations, deleteAutomation } from 'services/AutomationsService';
import { getLiteral } from 'utils/getLiteral';
import { getSrcUserCircleAvatar } from 'utils/getSrcAvatar';
import { getUserSfmUrl } from 'utils/getUrl';
import { RouteOnMount, ensureRoute } from 'utils/routes';
import { stripDiacritics } from 'utils/strings';
import { successToast, errorToast } from 'utils/toast';
import { logEvent } from 'utils/tracking';

import AutomationActivationConfirmModal from './components/AutomationActivationConfirmModal';
import AutomationsCreateMenu from './components/AutomationsCreateMenu';
import AutomationsListEmptyView from './components/AutomationsListEmptyView';
import EnabledCell from './components/cells/EnabledCell';
import LogsModal from './components/LogsModal';
import {
    getAutomationEntityByValue,
    resolveAutomationEntityIcon,
    resolveAutomationTriggerIcon,
} from './utils';
import './styles.scss';

const initialState = {
    loading: true,
    error: false,
    rows: null,
};

const fuseOptions = {
    threshold: 0.2,
    keys: ['name', 'description', 'entity', 'trigger'],
    getFn: (obj, path) => {
        const value = Fuse.config.getFn(obj, path);
        return removeAccents(value?.trim());
    },
};

function reducer(state, action) {
    switch (action.type) {
        case 'load':
            return {
                ...state,
                loading: false,
                error: false,
                rows: action.rows,
            };
        case 'loading':
            return {
                ...state,
                loading: true,
                error: false,
                rows: null,
            };
        case 'error':
            return {
                ...state,
                rows: null,
                loading: false,
                error: true,
            };
        default:
            throw new Error('No action provided');
    }
}

const AutomationsList = memo(({ openBuilder }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [searchInput, setSearchInput] = useState();
    const [confirmModalName, setConfirmModalName] = useState('');
    const searchInputRef = useRef(null);
    const cachedData = useRef({});
    const deleteModalRef = useRef({});
    const confirmModalRef = useRef({});
    const debounce = useRef(null);
    const logsModalRef = useRef();

    const getRowNodeId = useCallback((data) => data.id, []);

    const get = useCallback(() => {
        getAutomations()
            .then((data) => {
                let finalData = data.map((d) => {
                    const { src, fallbackSrc } = getSrcUserCircleAvatar(
                        d.userModifiedId || d.userCreatedId,
                    );
                    const entity = getAutomationEntityByValue(d.entity);

                    let name = getLiteral(d?.trigger?.action?.title);
                    if (d?.trigger?.action?.canCreate) {
                        name += ` (${getLiteral('label_automation_event_creation_included')})`;
                    }

                    return {
                        ...d,
                        userModifiedSrc: src,
                        userModifiedPlaceholder: fallbackSrc,
                        userModifiedId: d.userModifiedId || d.userCreatedId,
                        userModifiedDescription:
                            d.userModifiedDescription || d.userCreatedDescription,
                        triggerIcon: resolveAutomationTriggerIcon(
                            d?.trigger?.action?.icon,
                            d?.trigger?.action?.value,
                        ),
                        trigger: name,
                        entityIcon: resolveAutomationEntityIcon(d.entity),
                        entity: entity?.labels?.singular
                            ? getLiteral(entity?.labels?.singular)
                            : d.entity,
                    };
                });
                cachedData.current = finalData || [];
                if (searchInputRef.current) {
                    const rawText = stripDiacritics(searchInputRef.current?.toLowerCase()?.trim());
                    const fuse = new Fuse(cachedData.current, fuseOptions);
                    const matchingOptions = fuse.search(rawText).map((result) => result.item);
                    dispatch({ type: 'load', rows: matchingOptions });
                } else dispatch({ type: 'load', rows: finalData });
            })
            .catch((e) => {
                console.error(e);
                dispatch({ type: 'error' });
            });
    }, []);

    const load = useCallback(() => {
        dispatch({ type: 'loading' });
        get();
    }, [get]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        return subscribe(`${REFRESH_TABLE}_automations`, load);
    }, [load]);

    const onDeleteRef = useCallback((ref) => {
        deleteModalRef.current = ref;
    }, []);

    const onConfirmModalRef = useCallback((ref) => {
        confirmModalRef.current = ref;
    }, []);

    const onLogsModalRef = useCallback((ref) => {
        logsModalRef.current = ref;
    }, []);

    const openConfirmModal = useCallback(({ name, onConfirm, onCancel }) => {
        setConfirmModalName(name);
        confirmModalRef.current?.open({ onConfirm, onCancel, showSaveInactive: false });
    }, []);

    const onDelete = useCallback(
        (id) => {
            logEvent({
                event: 'settings',
                submodule: 'automations',
                functionality: 'delete',
            });
            return deleteAutomation(id)
                .then(() => {
                    load();
                    successToast({
                        title: getLiteral('title_toast_success'),
                        text: getLiteral('label_success_delete'),
                    });
                })
                .catch((e) => {
                    console.error(e);
                    errorToast({
                        title: getLiteral('error_error'),
                        text: getLiteral('label_error_delete'),
                    });
                });
        },
        [load],
    );

    const tableProps = useMemo(
        () => ({
            components: {
                EnabledCell,
                TextPopoverCell,
                IconCell,
            },
            columnDefs: [
                {
                    colId: 'name',
                    field: 'name',
                    headerName: getLiteral('label_automation'),
                    headerComponent: 'headerTextCell',
                    headerComponentParams: {
                        showTotal: true,
                    },
                    cellRenderer: 'EnabledCell',
                    cellRendererParams: {
                        openConfirmModal,
                        reload: get,
                        open: (id) => ensureRoute(`/settings/automations/${id}`),
                    },
                    width: 400,
                    suppressSizeToFit: true,
                    pinned: 'left',
                },
                {
                    colId: 'description',
                    field: 'description',
                    headerName: getLiteral('label_automation_description'),
                    cellRenderer: 'TextPopoverCell',
                    minWidth: 250,
                },
                {
                    colId: 'entity',
                    field: 'entity',
                    headerName: getLiteral('label_triggering_entity'),
                    cellRenderer: 'IconCell',
                    cellRendererParams: {
                        iconValue: 'entityIcon',
                    },
                    minWidth: 250,
                },
                {
                    colId: 'trigger',
                    field: 'trigger',
                    headerName: getLiteral('label_triggering_event'),
                    cellRenderer: 'IconCell',
                    cellRendererParams: {
                        iconValue: 'triggerIcon',
                    },
                    minWidth: 250,
                },
                {
                    colId: 'userModifiedDescription',
                    field: 'userModifiedDescription',
                    headerName: getLiteral('label_automation_user_modifier'),
                    cellRenderer: 'avatarCell',
                    cellRendererParams: {
                        getUrl: (data) => {
                            const id = data.userModifiedId;
                            if (!id) return;
                            return getUserSfmUrl(id) || '';
                        },
                        otherFields: {
                            src: 'userModifiedSrc',
                            placeholder: 'userModifiedPlaceholder',
                        },
                    },
                    minWidth: 250,
                },
                {
                    colId: 'updateTime',
                    field: 'updateTime',
                    headerName: getLiteral('label_modified'),
                    cellRenderer: 'dateCell',
                    cellRendererParams: {
                        locale: () => Context?.config?.userData?.locale,
                        inputFormat: "yyyy-MM-dd'T'HH:mm:ss",
                        outputFormat: 'P p',
                    },
                    minWidth: 250,
                },
                {
                    colId: 'createTime',
                    field: 'createTime',
                    headerName: getLiteral('label_created'),
                    cellRenderer: 'dateCell',
                    cellRendererParams: {
                        locale: () => Context?.config?.userData?.locale,
                        inputFormat: "yyyy-MM-dd'T'HH:mm:ss",
                        outputFormat: 'P p',
                    },
                    minWidth: 250,
                },
            ],
            forceActionsRefresh: true,
            actions: [
                (row) => {
                    return {
                        icon: <Icon name="accessTime" onClick={() => openLogsModal(row)} />,
                        tooltip: { content: getLiteral('action_see_automation_history') },
                    };
                },
                (row) => {
                    return {
                        icon: (
                            <Icon
                                className={
                                    row.blocked
                                        ? 'fm-automation-settings__actions-icon--blocked'
                                        : undefined
                                }
                                name="contentCopy"
                                onClick={
                                    row.blocked
                                        ? undefined
                                        : () => openBuilder(row.id, { duplicate: true })
                                }
                            />
                        ),
                        tooltip: {
                            content: getLiteral(
                                row.blocked ? 'label_automation_stopped_desc_1' : 'action_duply',
                            ),
                        },
                    };
                },
                (row) => {
                    return {
                        icon: (
                            <Icon
                                className={
                                    row.blocked
                                        ? 'fm-automation-settings__actions-icon--blocked'
                                        : undefined
                                }
                                name="edit"
                                onClick={
                                    row.blocked
                                        ? undefined
                                        : () => ensureRoute(`/settings/automations/${row.id}`)
                                }
                            />
                        ),
                        tooltip: {
                            content: getLiteral(
                                row.blocked ? 'label_automation_stopped_desc_1' : 'action_edit',
                            ),
                        },
                    };
                },
                (row) => {
                    return {
                        icon: (
                            <Icon name="delete" onClick={() => deleteModalRef.current?.open(row)} />
                        ),
                        tooltip: { content: getLiteral('action_delete') },
                    };
                },
            ],
            rowClassRules: {
                'ag-row--disabled': ({ data }) => !data.enabled || data.blocked,
                'automations-list__row--blocked': ({ data }) => data.blocked,
            },
        }),
        [get, openBuilder, openConfirmModal, openLogsModal],
    );

    const getErrorView = useCallback(() => {
        return {
            title: getLiteral('error_an_error_occurred'),
            subtitle: getLiteral('error_reload_table'),
            button: (
                <Button type="secondary" onClick={load}>
                    {getLiteral('action_reload_table')}
                </Button>
            ),
        };
    }, [load]);

    const searchByText = useCallback((text) => {
        clearTimeout(debounce.current);
        if (!text) return dispatch({ type: 'load', rows: cachedData.current });
        const rawText = stripDiacritics(text?.toLowerCase()?.trim());
        debounce.current = setTimeout(() => {
            const fuse = new Fuse(cachedData.current, fuseOptions);
            const matchingOptions = fuse.search(rawText).map((result) => result.item);
            dispatch({ type: 'load', rows: matchingOptions });

            logEvent({
                event: 'settings',
                submodule: 'automations',
                functionality: 'search',
            });
        }, 250);
    }, []);

    const onChangeSearch = useCallback(
        (e) => {
            searchInputRef.current = e?.target?.value || '';
            setSearchInput(e?.target?.value || '');
            if (!e?.target?.value) searchByText();
        },
        [searchByText],
    );

    const onBlurSearch = useCallback(
        (text) => {
            searchByText(text);
        },
        [searchByText],
    );

    const openLogsModal = useCallback((...props) => {
        logsModalRef.current?.open(...props);
    }, []);

    const emptyViewProps = useMemo(() => {
        return {
            onCreate: () => openBuilder(),
            onTemplates: () => ensureRoute('/settings/automations/templates'),
        };
    }, [openBuilder]);

    return (
        <>
            <div className="fm-automations-settings__toolbar">
                <SearchBar
                    className="fm-automation-settings__search-bar"
                    placeholder={getLiteral('action_search_automation')}
                    isFullWidth
                    inputValue={searchInput}
                    onChange={onChangeSearch}
                    onBlurSearch={onBlurSearch}
                    forceBlurOnEnter
                    useAsSimpleSearch
                />
                <AutomationsCreateMenu openBuilder={openBuilder} />
            </div>
            <Spacer y={2} />
            <SectionList
                id="automations-list"
                className="automations-list"
                {...tableProps}
                useLoaderPlaceholder
                isLoading={state.loading}
                hasError={state.error}
                getRowNodeId={getRowNodeId}
                rows={state.rows}
                totalRowData={state.rows?.length}
                errorViewProps={getErrorView}
                emptyViewProps={emptyViewProps}
                customEmptyViewComponent={AutomationsListEmptyView}
            />
            <DeleteModal
                title={getLiteral('action_delete')}
                onRef={onDeleteRef}
                onDelete={onDelete}
            />
            <AutomationActivationConfirmModal onRef={onConfirmModalRef} name={confirmModalName} />
            <LogsModal onRef={onLogsModalRef} />
            <Routes>
                <Route
                    path="/logs/:id"
                    element={
                        <RouteOnMount
                            onMount={(params) => {
                                if (!params.id) return;
                                logsModalRef.current?.open({
                                    id: params.id,
                                });
                            }}
                        />
                    }
                />
            </Routes>
        </>
    );
});

export default AutomationsList;
